import {Box} from 'src/design/atoms/Box';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import React, {useState} from 'react';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {ProjectSelectBox} from 'src/app/databound-elements/ProjectSelectBox';
import {SidebarLinkOsha} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {SelectBox} from 'src/design/atoms/SelectBox';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetOshaPdfMutation} from 'src/redux/queries/osha';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useDocumentDownload} from 'src/shared/hooks/useDocumentDownload';
import {useProjectScope} from 'src/shared/hooks/useProjectScope';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import type {ISelectOption} from 'src/types/Form';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {OshaEmploymentCard} from './components/OshaEmploymentCard';
import {OshaEstablishmentCard} from './components/OshaEstablishmentCard';
import {OshaSummaryCard} from './components/OshaSummaryCard';

const YEARS_BACK = 5;

const CURRENT_YEAR = new Date().getFullYear();

const YEAR_OPTIONS: ISelectOption[] = Array.from(
  {length: YEARS_BACK + 1},
  (_, i) => {
    const year = CURRENT_YEAR - i;
    return {id: year, label: String(year)};
  }
);

const OshaLogPage: React.FC = () => {
  useSetDocumentTitle('OSHA');
  const {download} = useDocumentDownload();
  const perms = useModulePermissions(ModuleEnum.Osha);
  const projectScope = useProjectScope();

  const [year, setYear] = useState<number>(CURRENT_YEAR);
  const [pickedProjectId, setPickedProjectId] = useState<number | null>(null);
  const [getPdf, {isLoading: isDownloading}] = useGetOshaPdfMutation();

  const projectId = projectScope?.id ?? pickedProjectId;

  const handleLoadPdf = (id: number) =>
    download(
      timeZone => getPdf({projectId: id, year, timeZone}).unwrap(),
      `osha-300a-${year}.pdf`
    );

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Osha}>
      <Stack gap="lg">
        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkOsha.icon}
            subtitle="OSHA 300A annual injury and illness summary">
            OSHA
          </PageHeader.Title>
          <PageHeader.Actions>
            {projectId !== null && (
              <PageHeader.Action
                variant="export"
                onClick={() => handleLoadPdf(projectId)}
                loading={isDownloading}>
                Load PDF
              </PageHeader.Action>
            )}
          </PageHeader.Actions>
        </PageHeader>

        <Group align="flex-end">
          <SelectBox
            label="Year"
            ariaLabel="Year"
            options={YEAR_OPTIONS}
            value={String(year)}
            onSelectChangeCallback={value => {
              if (value) setYear(Number(value));
            }}
            wrapperProps={{mb: 0, w: {base: '100%', sm: 160}}}
          />
          {projectScope === null && (
            <Box w={{base: '100%', sm: 260}}>
              <ProjectSelectBox
                label="Project"
                queryOptions={{}}
                value={pickedProjectId === null ? '' : String(pickedProjectId)}
                includeInstanceWide={false}
                placeholder="Select a project"
                onSelectChangeCallback={(option: ISelectOption | null) =>
                  setPickedProjectId(option?.id ? Number(option.id) : null)
                }
              />
            </Box>
          )}
        </Group>

        {projectId === null ? (
          <NoItemsPanel noItemsText="Select a project to view its OSHA 300A log." />
        ) : (
          <Grid gutter="lg" align="stretch">
            <Grid.Col span={{base: 12, lg: 6}}>
              <OshaSummaryCard projectId={projectId} year={year} />
            </Grid.Col>
            <Grid.Col span={{base: 12, lg: 6}}>
              <Stack gap="lg">
                <OshaEstablishmentCard projectId={projectId} />
                <OshaEmploymentCard
                  projectId={projectId}
                  year={year}
                  canUpdate={perms.update}
                />
              </Stack>
            </Grid.Col>
          </Grid>
        )}
      </Stack>
    </ContentWrapper>
  );
};

export default OshaLogPage;

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.Osha(),
  element: <OshaLogPage />,
  module: ModuleEnum.Osha,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
